/**
 * Status-derived helpers for the Engagement Hub.
 *
 * Owns the discriminated narrowing across spec §7 states so render sites
 * stay declarative. Three layers:
 *  - Type guards (`hasOffer`, `isTerminal`) for compile-time narrowing.
 *  - Copy helpers (`statusBadgeText`) for badge labels per spec §9.1 +
 *    §11.2 #9 (the two "awaiting your accept" copy variants).
 *  - Window helpers (`closingDaysRemaining`, `cancellationDaysRemaining`)
 *    for header banners on the closing/canceled stories.
 */
import * as Sentry from '@sentry/react';

import type {
  ActiveEngagement,
  CanceledEngagement,
  ClosedEngagement,
  ClosingEngagement,
  Engagement,
  EngagementOffer,
  PendingConfirmationEngagement,
} from '../../types';
import { formatDate } from './format';
import type { ViewerRole } from './roles';

/**
 * Engagement variants that have `EngagementOffer` fields guaranteed present.
 * Excludes draft_request (pre-send) and pending_offer (pre-offer).
 *
 * `CanceledEngagement` carries the offer fields as `Partial<>` — the
 * narrowing we expose here only includes a canceled engagement when the
 * offer was actually made before cancellation. The intersection with
 * `EngagementOffer` re-asserts the fields as required (TS intersects
 * `string | undefined` with `string` as `string`), so render sites can read
 * `engagement.expert_rate_amount_cents` etc. without non-null asserts.
 */
export type EngagementWithOffer =
  | PendingConfirmationEngagement
  | ActiveEngagement
  | ClosingEngagement
  | ClosedEngagement
  | (CanceledEngagement & EngagementOffer);

export function hasOffer(e: Engagement): e is EngagementWithOffer {
  if (e.status === 'pending_confirmation') return true;
  if (e.status === 'active') return true;
  if (e.status === 'closing') return true;
  if (e.status === 'closed') return true;
  if (e.status === 'canceled')
    return e.expert_offer_made_at !== null && e.expert_offer_made_at !== undefined;
  return false;
}

export function isTerminal(e: Engagement): e is ClosedEngagement | CanceledEngagement {
  return e.status === 'closed' || e.status === 'canceled';
}

/**
 * §6.6 — true when this engagement has crossed the funding-mode upward switch:
 * a credit-funded engagement whose threshold-crossing amendment flipped it to
 * `over_threshold_funding_method` (USD). Such an engagement is `usd_invoice`
 * now but carries `credit_draws` for its pre-switch periods and `invoices` for
 * its post-switch periods. Upward-only — there is no revert affordance.
 */
export function hasFundingSwitched(e: Engagement): boolean {
  return hasOffer(e) && e.funding_switched_at !== null && e.funding_switched_at !== undefined;
}

export function isMidFlightCanceled(
  e: Engagement
): e is CanceledEngagement & { cancellation_window_ends_at: string } {
  return (
    e.status === 'canceled' &&
    e.cancellation_window_ends_at !== null &&
    e.cancellation_window_ends_at !== undefined
  );
}

/**
 * Spec §9.1 + §11.2 #9 — derived from status + awaiting per the strictly-serial
 * v3 model. Customer always reviews first; governance is notified only on
 * customer accept (and only if threshold breached). The pre-accept threshold
 * warning is a banner concern, not a badge variant.
 *
 * Per spec §8.6 outcomes B/C: when `awaiting='customer'` AND
 * `governance_feedback` is non-null, the engagement has bounced back to the
 * customer creator and the badge surfaces the kind of mediation owed —
 * "Adjustment to review" / "Rejection to mediate". Same attention tone as
 * the ordinary "Awaiting your accept" state.
 *
 * Viewer-aware per the §9.6 locked copy table: the EXPERT sees third-person
 * framing in PENDING_CONFIRMATION — "Awaiting customer accept"
 * (awaiting='customer') / "Awaiting governance approval"
 * (awaiting='governance') — including the governance-feedback bounce states,
 * which the expert has not been notified about (§8.6: governance feedback
 * flows through the customer creator). Customer-side and OF copy unchanged.
 * `viewerRole` is optional so list-row call sites keep current behavior.
 */
export function statusBadgeText(e: Engagement, viewerRole?: ViewerRole): string {
  return statusBadgeTextFor(
    e.status,
    e.status === 'pending_confirmation' ? e.awaiting : null,
    viewerRole,
    e.status === 'pending_confirmation' ? (e.governance_feedback?.kind ?? null) : null
  );
}

/**
 * ENG-5448 (FE2a): the same copy table keyed by primitives, because live data
 * carries `status`/`awaiting` as plain strings rather than the prototype's
 * discriminated union. The union-typed `statusBadgeText` above delegates here
 * so there is one copy table, not two.
 *
 * `governanceFeedbackKind` comes from `governanceFeedback.kind` on the live Hub
 * (ENG-5604 closed gap G4). The list rows don't select it and pass nothing.
 */
export function statusBadgeTextFor(
  status: string,
  awaiting: string | null,
  viewerRole?: ViewerRole,
  governanceFeedbackKind?: 'adjustment' | 'rejection' | null
): string {
  switch (status) {
    case 'draft_request':
      return 'Draft';
    case 'pending_offer':
      return 'Awaiting offer';
    case 'pending_confirmation':
      if (awaiting === 'governance') return 'Awaiting governance approval';
      // Both of these read the engagement from outside the customer side, so
      // neither is the one being waited on. Without the second branch an OF
      // operator is told "Awaiting your accept" about an engagement they hold
      // no role on (ENG-5684) — wrong, and phrased as a call to action on a
      // surface that deliberately offers none (ENG-5683).
      if (viewerRole === 'expert' || viewerRole === 'of_fulfillment')
        return 'Awaiting customer accept';
      if (governanceFeedbackKind === 'adjustment') return 'Adjustment to review';
      if (governanceFeedbackKind === 'rejection') return 'Rejection to mediate';
      return 'Awaiting your accept';
    case 'active':
      return 'Active';
    case 'closing':
      return 'Closing';
    case 'closed':
      return 'Closed';
    case 'canceled':
      return 'Canceled';
    default:
      reportUnknownStatus(status);
      return 'Status unavailable';
  }
}

/**
 * A status the api grew that this build doesn't know about. Renders neutrally
 * rather than crashing (plan §2), and leaves a breadcrumb so the next Sentry
 * event on this session shows which value we couldn't read.
 */
function reportUnknownStatus(status: string): void {
  Sentry.addBreadcrumb({
    category: 'engagement.status',
    level: 'warning',
    message: 'Unknown engagement status',
    data: { status },
  });
}

/**
 * Visual tone for the status badge. Mirrors the tone semantics from the
 * Hours tab StatusPill so non-action states (active/closed) look quiet,
 * action-needed states (awaiting/closing/canceled) carry a colored leading
 * dot.
 */
export type StatusTone = 'active' | 'attention' | 'progress' | 'muted' | 'neutral';

export function statusBadgeTone(e: Engagement): StatusTone {
  return statusBadgeToneFor(e.status);
}

/** String-keyed twin of `statusBadgeTone` — see `statusBadgeTextFor`. */
export function statusBadgeToneFor(status: string): StatusTone {
  switch (status) {
    case 'draft_request':
      return 'neutral';
    case 'pending_offer':
    case 'pending_confirmation':
      return 'attention';
    case 'active':
      return 'active';
    case 'closing':
      return 'progress';
    case 'closed':
    case 'canceled':
      return 'muted';
    default:
      return 'neutral';
  }
}

const CLOSING_WINDOW_DAYS = 14;

/**
 * Whole-day diff between two timestamps, normalized to UTC midnight on each
 * side so the time-of-day component doesn't bleed a day boundary either way.
 * Returned value can be negative if `to` is before `from`.
 *
 * Exported for the §8.10 ghost-expert trigger math in `ghost-expert.ts`,
 * which needs identical day semantics to the window helpers here.
 */
export function wholeDaysBetween(from: Date, to: Date): number {
  const fromDay = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate());
  const toDay = Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate());
  return Math.round((toDay - fromDay) / 86400000);
}

/**
 * Days remaining in the 14-day CLOSING admin window. Spec §7: window starts
 * at `closing_initiated_at`. Returns 0 when expired (still rendered for
 * one render cycle before transitioning to CLOSED).
 */
export function closingDaysRemaining(e: ClosingEngagement, now: Date): number {
  const start = new Date(e.closing_initiated_at);
  const end = new Date(start.getTime() + CLOSING_WINDOW_DAYS * 86400000);
  return Math.max(0, wholeDaysBetween(now, end));
}

/**
 * Days remaining in the 7-day mid-flight cancellation reconciliation window.
 * Spec §8.9.
 */
export function cancellationDaysRemaining(
  e: CanceledEngagement & { cancellation_window_ends_at: string },
  now: Date
): number {
  const end = new Date(e.cancellation_window_ends_at);
  return Math.max(0, wholeDaysBetween(now, end));
}

/**
 * The closure date — when the engagement officially closed and no new work
 * is authorized. Equal to `closing_initiated_at`. The 14-day admin window
 * is only for paperwork (submitting hours for pre-closure work).
 */
export function closureDateLabel(e: ClosingEngagement): string {
  return formatDate(e.closing_initiated_at);
}
